import { Edge, GroupNode, Node } from 'ng-diagram';
import { EventDirection } from '../definition/workflow';

/**
 * The ng-diagram graph of one modeler perspective, and the vocabulary every perspective shares.
 *
 * Nothing here is a model of its own. The nodes and edges are ng-diagram's, so a converted graph can be
 * handed straight to `initializeModel`, and the two `data` shapes below are the only thing this module
 * adds — they are how base-workflow's own vocabulary survives the trip through a generic diagram library.
 *
 * Deliberately free of any one perspective. The Roles modeler is the first of three — Tasks and Workflows
 * follow — and what differs between them is which elements are drawn and what an edge between two of them
 * means, not how an element is represented. That difference lives in a converter
 * ({@link RoleResponsibilityGraphConverter} is the first), and everything else in `domain/modeler` and
 * `feature/modeler` is shared.
 */

/**
 * The `type` every modeler node carries, and the key `NgDiagramNodeTemplateMap` resolves
 * {@link WorkflowElementNodeComponent} by. Prefixed, because the map is one flat registry per diagram and
 * `element` is a name any feature might plausibly claim.
 *
 * One type for all five kinds rather than one per kind: every element is drawn the same way — its symbol
 * and its name — and {@link WorkflowNodeData.kind} is what picks the symbol. Five registrations would be
 * five templates that had to be kept looking alike.
 */
export const WORKFLOW_NODE_TYPE = 'ppWorkflowElement';

/**
 * The `type` of a **lane** — the ng-diagram group node the Workflows perspective puts one of per performing
 * role, and the key {@link WorkflowLaneNodeComponent} is registered under.
 *
 * A second node type rather than a sixth {@link WorkflowElementKind}, because a lane is not drawn like an
 * element at all: it is a band the width of the whole diagram with a header down its left edge, and
 * ng-diagram resolves a group node through a template of its own shape
 * (`NgDiagramGroupNodeTemplate`). Its `data` is still {@link WorkflowNodeData} with `kind: 'role'`, so the
 * header's symbol and name come from the same two helpers as every other element's.
 */
export const WORKFLOW_LANE_TYPE = 'ppWorkflowLane';

/**
 * The `type` of an edge that says *which* relation it is, and the key
 * {@link WorkflowRelationEdgeComponent} is registered under in the canvas's edge template map.
 *
 * Only the Workflows perspective sets it. A Roles edge leaves `type` unset and so keeps ng-diagram's own
 * default edge template, exactly as before — one relation on screen needs no distinguishing.
 */
export const WORKFLOW_RELATION_EDGE_TYPE = 'ppWorkflowRelation';

/**
 * What a node stands for, which is also which symbol it is drawn with — files in `src/assets/modeler`. See
 * {@link modelerIconUrl}.
 *
 * The first five are base-workflow's routable aggregates minus the instance layer: a modeler draws what a
 * tenant *authors*. A run is monitored on the generated instance screens, and drawing it would need a
 * different vocabulary again (a step that has finished, a task waiting on someone).
 *
 * `start` and `end` are the Workflows perspective's BPMN events. A `start` node is one `StartEvent` of the
 * workflow; the `end` node is *derived* — the model has no end element, and the one drawn stands for every
 * task nothing depends on having finished. An `event` node is one intermediate `EventUse`, a throw or a
 * catch by its {@link WorkflowNodeData.direction} — or, with {@link WorkflowNodeData.attachedTo} set, a
 * boundary event on the border of the task it is attached to.
 */
export type WorkflowElementKind = 'role' | 'artifact' | 'task' | 'tool' | 'workflow' | 'start' | 'end' | 'event';

/** The flow's own kinds — the ones that ride `sequence` edges and take a column of the flow. */
export const FLOW_KINDS: readonly WorkflowElementKind[] = ['task', 'start', 'end', 'event'];

/**
 * The box a start, intermediate or end event is drawn as: a circle, its name written underneath rather than inside.
 *
 * Stated on the node with `autoSize: false` by the converter rather than measured, because both layouts
 * read it — the flat one to centre the circle on its Dagre position, the swimlane one to centre it in its
 * cell — and ng-diagram discards an explicit `size` on an `autoSize` node.
 *
 * 64px: large enough that the event symbol — a drawing twice as wide as it is high — reads inside the
 * circle, and small enough that, centred in a 76px swimlane row, the one-line name under it still ends
 * inside the lane's bottom padding.
 */
export const EVENT_NODE_SIZE = { width: 64, height: 64 };

/**
 * The box a boundary event is drawn as: a smaller circle, so that pinned across the lower edge of a 76px-high
 * task card it covers no more of the card than its bottom padding.
 *
 * Stated with `autoSize: false` for the same reason as {@link EVENT_NODE_SIZE}: the layouts read it to centre
 * the circle on the card's edge.
 */
export const BOUNDARY_EVENT_NODE_SIZE = { width: 36, height: 36 };

/**
 * What a modeler node carries in ng-diagram's `data`.
 *
 * `label` and `description` rather than the domain object itself, which is where this departs from
 * base-state's `StateNodeData`: that modeler *edits* the state behind a node, so its properties panel needs
 * the whole `State`. These diagrams are read-only, and a node whose data is already what the template draws
 * is a node the same template can draw whatever aggregate it came from — which is what lets one node
 * component serve all five kinds.
 */
export interface WorkflowNodeData {
  kind: WorkflowElementKind;
  /**
   * The id of the catalog entry behind this node — the raw one, without the `kind:` prefix the node's own
   * id carries. Set so that the properties panel can name what it is showing without having to take a
   * composite node id back apart; the prefix is this module's business, not a panel's.
   *
   * Optional because a node's `data` is what a *template* draws, and neither the card nor the lane draws
   * an id. A perspective that has no panel need not fill it in.
   */
  elementId?: string;
  /** What the element is called: its `name`, or its id when it has no name. */
  label: string;
  /** Shown under the label when the element has one. */
  description?: string;
  /**
   * True for the one element the diagram was opened *from* — the role whose Modeler tab this is. Marks it
   * without changing what is drawn: the whole organisation is on screen either way.
   */
  highlighted?: boolean;
  /**
   * True for an element that exists only as a reference: an id named by another element that the catalog it
   * should live in does not contain. Drawn rather than dropped, because a dangling reference is a fact
   * about the model worth seeing — and silently omitting it would show a role as responsible for less than
   * it claims.
   */
  unresolved?: boolean;
  /**
   * Where a running instance of the workflow stands on this element — the `TaskInstanceStatus` of a task,
   * the `EventInstanceStatus` of an event. Set only when the diagram is drawn for one run, as the task
   * dashboard's Process view does; the modeler draws definitions and leaves it unset.
   */
  runStatus?: string;
  /**
   * An intermediate event's direction — whether it raises its catalog event or waits for it, which picks
   * the filled or the outlined symbol. Unset on every other kind, and on an event row not yet given one.
   */
  direction?: EventDirection;
  /**
   * True for an event that waits for a timer rather than a catalog event, which draws it with the clock.
   * Read through `timerOf`, so a timer typed into the form but not saved yet is drawn as one too.
   */
  timer?: boolean;
  /**
   * A boundary event's host: the node id of the task it is attached to. The layouts pin the event to the
   * host's lower edge rather than giving it a column of its own — see `placeBoundaryEvents`.
   */
  attachedTo?: string;
  /**
   * A boundary event's `interrupting`, resolved — `true` where the model leaves it absent. A solid double
   * ring when true, a dashed one when not. Unset on every other node.
   */
  interrupting?: boolean;
  /**
   * A task's guards and steps, as its catalog entry states them — shown by the properties panel, never drawn.
   * The two rules are base-rule ids, kept as ids because this library has no rule store to name them from.
   * Unset on every other kind, and on a task whose catalog entry does not resolve.
   */
  task?: WorkflowTaskDetails;
}

/** What {@link WorkflowNodeData.task} carries: the parts of a `TaskDefinition` a node does not draw. */
export interface WorkflowTaskDetails {
  preconditionRuleId?: string;
  postconditionRuleId?: string;
  /** The step names in declaration order — the order the dashboard's checklist shows them in. */
  steps: string[];
}

/**
 * What one edge *means*, which is also how it is drawn — see {@link WorkflowRelationEdgeComponent}.
 *
 * The split that matters is `sequence`/`implicit` against the rest: the first two are the workflow's
 * control flow and are drawn solid, everything else is a data or tool association and is drawn dashed, the
 * same distinction BPMN makes between a sequence flow and an association.
 *
 * - `sequence` — a `dependsOn` entry of a task or an intermediate event, the only flow relation the model
 *   states outright; and the two the start and end events add: a start event to each task or intermediate
 *   event that depends on nothing, and each one nothing depends on to the end event.
 * - `implicit` — the order two `parallel: false` siblings sharing a `dependsOn` set actually run in, which
 *   is their position in `Workflow.tasks` and exists nowhere as data. Drawn distinctly rather than as a
 *   plain sequence edge, so a reader can tell a declared dependency from one inferred from declaration
 *   order — the second changes when the rows are reordered and the first does not.
 * - `input` / `output` — a `TaskDefinition.inputs` / `.outputs` entry.
 * - `tool` — a `SERVICE_STEP`'s `toolDefinitionId`.
 * - `start` — a `StartEvent.requiredArtifacts` entry feeding the start event it belongs to.
 */
export type WorkflowRelation = 'sequence' | 'implicit' | 'input' | 'output' | 'tool' | 'start';

/**
 * What a modeler edge carries in ng-diagram's `data`.
 *
 * Both fields are optional and both are unused by the Roles perspective: a line from a role to an artifact
 * already reads as responsibility, and the one relation on screen needs neither naming nor distinguishing.
 * The Workflows perspective, where an edge is a dependency, an input, an output or a tool call, is what
 * they are here for.
 */
export interface WorkflowEdgeData {
  /** Which relation this is. Absent leaves the edge on ng-diagram's default template. */
  relation?: WorkflowRelation;
  /** Written at the edge's midpoint when set. Used for the `ANY` join, the model's only gateway. */
  label?: string;
  /**
   * True for a flow edge the open run has travelled — set only when the diagram is drawn for one run, as the
   * task dashboard's Process view does. Drawn in the "done" green, like the finished elements it joins.
   */
  passed?: boolean;
}

export type WorkflowNode = Node<WorkflowNodeData>;
export type WorkflowLaneNode = GroupNode<WorkflowNodeData>;
export type WorkflowEdge = Edge<WorkflowEdgeData>;

/**
 * Whether a node is a lane rather than an element.
 *
 * ng-diagram's `Node` is a union of `SimpleNode | GroupNode`, so `node.isGroup` does not type-check on one
 * — the property exists on only one arm. This is the same `'isGroup' in node` test the library narrows with
 * internally, named once here rather than repeated at each of the half-dozen places that has to sort the
 * lanes out from what is in them.
 */
export function isLaneNode(node: WorkflowNode): node is WorkflowLaneNode {
  return 'isGroup' in node;
}

/** Whether a node is a boundary event — one the layouts pin to its host task rather than place. */
export function isBoundaryNode(node: WorkflowNode): boolean {
  return !isLaneNode(node) && !!node.data.attachedTo;
}

/**
 * A converted perspective, ready for `initializeModel` once the nodes have been through
 * {@link WorkflowLayoutService}.
 *
 * No viewport and no "which nodes are unplaced", both of which `StateMachineGraph` carries — and both stay
 * absent now that the Workflows perspective does persist an arrangement. The viewport belongs to
 * `WorkflowDiagram`, the resource that stores it, rather than to the projection of the workflow; and there is
 * no unplaced list because the layout service places *every* node before a saved arrangement is applied over
 * it, so a node the arrangement does not mention is not unplaced, merely un-overridden.
 */
export interface WorkflowGraph {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}

/**
 * The node id of one element, which is its kind and its own id.
 *
 * Prefixed by kind because the ids are only unique *within* a catalog: `/roles/{roleId}` and
 * `/artifacts/{artifactId}` are separate resources, so a tenant may perfectly well have a role and an
 * artifact both called `order`. Unprefixed, the two would be one node and the diagram would draw the role
 * as responsible for itself.
 */
export function elementNodeId(kind: WorkflowElementKind, id: string): string {
  return `${kind}:${id}`;
}

/**
 * The edge id of one relation, derived from the two node ids it joins.
 *
 * Derived rather than generated, so rebuilding the graph — which every reload and every catalog change
 * does — produces the same ids as before. A generated id would make ng-diagram treat an unchanged edge as
 * a new one.
 */
export function elementEdgeId(sourceNodeId: string, targetNodeId: string): string {
  return `${sourceNodeId}->${targetNodeId}`;
}

/**
 * The node id of the lane a role performs its tasks in.
 *
 * Distinct from `elementNodeId('role', roleId)` on purpose. A lane and a role card are two different things
 * a diagram may hold at once — the Roles perspective draws the card, the Workflows perspective draws the
 * lane — and sharing one id would mean a graph could not carry both, which is the kind of constraint that
 * only shows up as a silently missing node.
 */
export function laneNodeId(roleId: string): string {
  return `lane:${roleId}`;
}
